"use client";

import { useCallback, useEffect, useState, type FC } from "react";
import { useForm } from "react-hook-form";
import { useTranslations, useLocale } from "next-intl";
import Link from "next/link";

import type { Field as FieldType } from "@types";
import { getAirtableFormBase } from "utils";
import { SLUGS, CONTACT_GTM_EVENT, CAMPAIGN_GTM_EVENT, FormType } from "consts";
import { submitForm as submitFormService } from "services/form";

import { Spacer } from "components/organisms/Spacer/Spacer";
import { Loader } from "components/atoms/Loader/Loader";
import classNames from "classnames";

import FieldComponent from "./fields/Field";
import { SubmitButton } from "./fields/SubmitButton";
import s from "./Form.module.scss";
import { getPublicLocale, type Locale } from "consts/locale";

type Props = {
  fields: FieldType[];
  formType: FormType;
  defaultValues?: any;
  transformValuesBeforeSubmit?: (values: Record<string, unknown>) => Record<string, unknown>;
  className?: string;
  title?: string;
  intro?: string;
};

export const Form: FC<Props> = ({
  title,
  intro,
  fields,
  formType,
  defaultValues,
  transformValuesBeforeSubmit,
  className,
}) => {
  const t = useTranslations("search");
  const locale = useLocale() as Locale;
  const { register, watch, handleSubmit, reset, control } = useForm({
    defaultValues,
  });

  useEffect(() => {
    reset(defaultValues, {
      keepDirtyValues: true,
    });
  }, [defaultValues, reset]);

  const [pending, setPending] = useState(false);
  const [formSent, setFormSent] = useState(false);
  const [serverError, setServerError] = useState<null | string>(null);

  const trackSubmit = useCallback(() => {
    if (typeof window === "undefined") return;

    const dataLayer = window.dataLayer || [];
    const event = formType === FormType.campaign ? CAMPAIGN_GTM_EVENT : CONTACT_GTM_EVENT;

    // Build form type dynamically
    const formTypeMap = {
      general: "contact",
      jobs: "jobs",
      partners: "kantoren",
      campaign: "campaign",
    };

    const whichForm = `form_${formTypeMap[formType]}_${locale}`;
    dataLayer.push({ event, which_form: whichForm });
  }, [formType, locale]);

  const onSubmit = useCallback(
    async (values: any) => {
      setPending(true);
      setServerError(null);

      trackSubmit();
      const submissionValues = transformValuesBeforeSubmit
        ? transformValuesBeforeSubmit(values)
        : values;
      const response = await submitFormService(
        submissionValues,
        getAirtableFormBase(formType, locale),
        formType,
      );

      if (response.success) {
        setFormSent(true);
      } else {
        setServerError(t("error-form"));
      }
      setPending(false);
    },
    [locale, formType, t, trackSubmit, transformValuesBeforeSubmit],
  );

  const privacyLink = `${SLUGS.page}/${
    getPublicLocale(locale) === "nl" ? "privacyverklaring" : "politque-de-vie-privee"
  }`;

  if (formSent) {
    return (
      <div className={classNames(s.Success, className)}>
        <Spacer size="s">
          <h2>{t("form-submit-success-title")}</h2>
          <p>
            {t("form-submit-success-text")}{" "}
            <Link href={privacyLink}>{t("form-submit-success-privacy")}</Link>.
          </p>
        </Spacer>
      </div>
    );
  }

  return (
    <>
      {title && <h2 className={s.Title}>{title}</h2>}
      {intro && (
        <div className={s.Intro}>
          <p>{intro}</p>
        </div>
      )}
      <form onSubmit={handleSubmit(onSubmit)} className={className}>
        <fieldset>
          <Spacer size="s">
            {fields.map((field) => (
              <FieldComponent
                key={field.id}
                field={field}
                register={register}
                watch={watch}
                control={control}
              />
            ))}
            {serverError && <p className={s.ErrorMessage}>{serverError}</p>}
            <div className={s.Submit}>
              <SubmitButton value={t("send")} disabled={pending} />
              {pending && <Loader />}
            </div>
          </Spacer>
        </fieldset>
      </form>
    </>
  );
};
